import { useEffect, useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EditableCell } from "@/components/admin/EditableCell";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtInt, fmtNok } from "@/lib/format";

const WD = ["Søn", "Man", "Tir", "Ons", "Tor", "Fre", "Lør"];

const Detail = ({ s, cid, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const editDay = async (date, count) => {
    try {
      const days = s.days.map((d) => ({ date: d.date, count: d.date === date ? count : d.count }));
      const { data } = await api.patch(`/customers/${cid}/pax/submissions/${s.id}`, { days });
      onUpdated(data);
      toast.success("Passasjertallet er endret og logget");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/customers/${cid}/pax/submissions/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `passasjerrapport-${s.rederi}-${s.period_start}.pdf`.replace(/[^\w.-]+/g, "-");
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-3" data-testid={`pax-sub-detail-${s.id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        {[["Kontaktperson", s.contact], ["E-post", s.email], ["Telefon", s.phone], ...(s.manual ? [["Mottatt", s.received_date.split("-").reverse().join(".")], ["Registrert av", s.registered_by]] : [["Innsendt", fmtDate(s.submitted_at)]])].map(([k, v]) => (
          <div key={k}><div className="text-xs text-slate-500">{k}</div><div className="font-medium text-slate-900 break-words">{v}</div></div>
        ))}
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5">
        {s.days.map((d) => {
          const dt = new Date(`${d.date}T12:00:00`);
          return (
            <div key={d.date} className="rounded border border-slate-200 text-center pt-1.5 pb-0.5">
              <div className="text-[11px] text-slate-500">{WD[dt.getDay()]} {dt.getDate()}.{dt.getMonth() + 1}.</div>
              <EditableCell value={d.count} format={(v) => String(v)} onCommit={(v) => Number.isInteger(v) ? editDay(d.date, v) : toast.error("Bruk hele tall")} testId={`pax-day-${s.id}-${d.date}`} className="font-semibold text-slate-900 justify-center min-w-0" />
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-500">Klikk på et passasjertall for å rette det. Alle endringer lagres i endringsloggen.{s.edited ? " Denne innsendingen er endret av havnen." : ""}</p>
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <span>{fmtInt(s.total_pax)} passasjerer × {fmtNok(s.price)} = {fmtNok(s.amount)}{s.late_fee ? ` + gebyr ${fmtNok(s.late_fee)}` : ""} · <b>Totalt {fmtNok(s.total_amount)}</b></span>
        <Button size="sm" onClick={download} disabled={busy} data-testid={`pax-sub-pdf-${s.id}`}>{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Last ned PDF</Button>
      </div>
    </div>
  );
};

export const ActorDialog = ({ cid, open, onClose, onChanged, onManual }) => {
  const [data, setData] = useState(null);
  const [openSub, setOpenSub] = useState(null);
  useEffect(() => {
    if (!open?.actorId) return;
    setData(null);
    api.get(`/customers/${cid}/pax/actors/${open.actorId}`).then((r) => {
      setData(r.data);
      setOpenSub(open.subId || r.data.submissions[0]?.id || null);
    }).catch((e) => toast.error(errMsg(e)));
  }, [cid, open]);

  const deactivate = async () => {
    if (!window.confirm("Fjerne rederiet fra oversikten? Innsendte rapporter beholdes.")) return;
    try {
      await api.patch(`/customers/${cid}/pax/actors/${open.actorId}`, { active: false });
      toast.success("Rederiet er fjernet fra oversikten");
      onChanged();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="pax-actor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{data?.actor.name || "Laster…"}</DialogTitle>
          <DialogDescription>{data ? `${data.submissions.length} innsendinger` : ""}</DialogDescription>
        </DialogHeader>
        {!data ? <Loader2 className="w-5 h-5 animate-spin text-sky-700" /> : (
          <div className="space-y-2">
            {data.submissions.length === 0 && <p className="text-sm text-slate-500">Ingen innsendinger ennå.</p>}
            {data.submissions.map((s) => (
              <div key={s.id} className="rounded-md border border-slate-200 p-3">
                <button type="button" className="w-full flex flex-wrap items-center justify-between gap-2 text-left" onClick={() => setOpenSub(openSub === s.id ? null : s.id)} onDoubleClick={() => setOpenSub(s.id)} data-testid={`pax-sub-${s.id}`}>
                  <span className="font-medium text-slate-900">{s.period_label}{s.manual && <span className="ml-2 rounded-full bg-sky-50 text-sky-800 border border-sky-100 px-2 py-0.5 text-[11px] font-medium" title={`Registrert av ${s.registered_by}`}>Manuelt registrert</span>}</span>
                  <span className="flex items-center gap-2 text-sm">
                    <span className="text-slate-600">{fmtInt(s.total_pax)} pass.</span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${s.late ? "bg-amber-50 text-amber-800" : "bg-emerald-50 text-emerald-800"}`}>{s.late ? "For sent" : "I tide"}</span>
                  </span>
                </button>
                {openSub === s.id && <Detail s={s} cid={cid} onUpdated={(u) => { setData((p) => ({ ...p, submissions: p.submissions.map((x) => (x.id === u.id ? u : x)) })); onChanged(); }} />}
              </div>
            ))}
            <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
              <Button variant="outline" size="sm" onClick={() => onManual(open.actorId)} data-testid="pax-actor-manual">Registrer rapport manuelt</Button>
              <Button variant="ghost" size="sm" className="text-red-600" onClick={deactivate} data-testid="pax-actor-deactivate">Fjern rederiet fra oversikten</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
